import { useEffect, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import type { PanelEntityType } from "@/v2/stores/panelStore";
import { usePanelStore } from "@/v2/stores/panelStore";

const PANEL_PARAM = "panel";

function encodePanelParam(
  entityType: PanelEntityType,
  entityId: string
): string {
  return `${entityType}:${entityId}`;
}

function decodePanelParam(
  value: string
): { entityType: PanelEntityType; entityId: string } | null {
  const colonIndex = value.indexOf(":");
  if (colonIndex === -1) return null;

  const entityType = value.slice(0, colonIndex) as PanelEntityType;
  const entityId = value.slice(colonIndex + 1);

  const validTypes: PanelEntityType[] = [
    "rental",
    "vehicle",
    "station",
    "dock",
    "user",
    "reservation",
    "transaction",
    "city",
    "lock",
  ];
  if (!validTypes.includes(entityType) || !entityId) return null;

  return { entityType, entityId };
}

export function usePanelUrlSync() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { stack, isOpen, openPanel, closePanel } = usePanelStore();

  const prevPathnameRef = useRef(location.pathname);
  const prevStackRef = useRef(stack);

  const didHydrateRef = useRef(false);

  useEffect(() => {
    if (didHydrateRef.current) return;
    didHydrateRef.current = true;

    const panelParam = searchParams.get(PANEL_PARAM);
    if (panelParam && stack.length === 0) {
      const decoded = decodePanelParam(panelParam);
      if (decoded) {
        openPanel(decoded);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stack, searchParams, openPanel]);

  useEffect(() => {
    if (prevPathnameRef.current !== location.pathname) {
      prevPathnameRef.current = location.pathname;
      if (isOpen) {
        closePanel();
      }
    }
  }, [location.pathname, isOpen, closePanel]);

  useEffect(() => {
    const prevStack = prevStackRef.current;
    prevStackRef.current = stack;

    if (prevStack === stack) return;

    const topEntry = stack.length > 0 ? stack[stack.length - 1] : null;
    const currentParam = searchParams.get(PANEL_PARAM);

    if (topEntry) {
      const encoded = encodePanelParam(topEntry.entityType, topEntry.entityId);
      if (currentParam !== encoded) {
        const isFirstEntry = stack.length === 1 && prevStack.length === 0;
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set(PANEL_PARAM, encoded);
            return next;
          },
          { replace: isFirstEntry }
        );
      }
    } else if (currentParam) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(PANEL_PARAM);
          return next;
        },
        { replace: true }
      );
    }
  }, [stack, searchParams, setSearchParams]);

  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const panelParam = params.get(PANEL_PARAM);
      const { stack: currentStack, isOpen: currentIsOpen } =
        usePanelStore.getState();

      if (panelParam) {
        const decoded = decodePanelParam(panelParam);
        if (!decoded) return;

        const topEntry =
          currentStack.length > 0
            ? currentStack[currentStack.length - 1]
            : null;
        const currentEncoded = topEntry
          ? encodePanelParam(topEntry.entityType, topEntry.entityId)
          : null;

        if (currentEncoded === panelParam) return;

        if (
          currentStack.length > 1 &&
          encodePanelParam(
            currentStack[currentStack.length - 2].entityType,
            currentStack[currentStack.length - 2].entityId
          ) === panelParam
        ) {
          usePanelStore.getState().popPanel();
        } else {
          usePanelStore.getState().openPanel(decoded);
        }
      } else if (currentIsOpen) {
        usePanelStore.getState().closePanel();
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);
}
